import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Divider, Flex, Form, InputNumber, Row, Space, Typography } from 'antd';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getSevenDaysLogin, updateDailyRewardMission } from '@/services/seven_days_login';
import { useMutation } from '@tanstack/react-query';
import { formatDate, formatNumber } from '@/utils/formatters';
import { toast } from 'react-toastify';
import AppTable from '@/components/AppTable';
import AppSearch from '@/components/AppSearch';
import AppTableQuery from '@/components/AppTableQuery';
import AppPageTitle from '@/components/AppPageTitle';

const START_DATE = dayjs().startOf('month').format('YYYY-MM-DD');
const END_DATE = dayjs().endOf('month').format('YYYY-MM-DD');
const { Text } = Typography;
export default function LoginStreak() {
  const [time, setTime] = useState([START_DATE, END_DATE]);
  const [searchValue, setSearchValue] = useState(null);
  const [searchValueDaysCompleted, setSearchValueDaysCompleted] = useState(null);
  const [query, setQuery] = useState({
    isdn: null,
    start_date: time[0],
    end_date: time[1],
    days_completed: null,
  });
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const offset = (pageIndex - 1) * pageSize + 1;
  const { data, isLoading, isRefetching, refetch } = usePaginatedQuery(
    () =>
      getSevenDaysLogin({
        ...query,
        limit: pageSize,
        offset: offset,
      }),
    ['seven-days-login', query, pageSize, offset]
  );
  const { mutate: updateDailyReward, isPending } = useMutation({
    mutationFn: (data) => updateDailyRewardMission(data),
  });
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Date Real Time',
      dataIndex: 'created_at',
      key: 'date_real_time',
      render: (created_at) => formatDate(created_at, 'DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'ISDN',
      dataIndex: 'isdn',
      key: 'isdn',
    },
    {
      title: 'Cycle ID',
      dataIndex: 'cycle_id',
      key: 'cycle_id',
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (start_date) => formatDate(start_date, 'DD/MM/YYYY'),
    },
    {
      title: 'Current Day',
      dataIndex: 'current_day',
      key: 'current_day',
    },
    {
      title: 'Days Completed',
      dataIndex: 'days_completed',
      key: 'days_completed',
      render: (days_completed) => (days_completed ? formatNumber(days_completed) : 0),
    },
    {
      title: 'Dropped At Day',
      dataIndex: 'dropped_at_day',
      key: 'dropped_at_day',
    },
  ];
  const handleQuery = () => {
    setQuery({
      isdn: searchValue?.trim(),
      start_date: time[0],
      end_date: time[1],
      days_completed: searchValueDaysCompleted,
    });
    setPageIndex(1);
  };
  const handleReset = () => {
    setQuery({
      isdn: null,
      start_date: null,
      end_date: null,
      days_completed: null,
    });
    setSearchValue(null);
    setTime([START_DATE, END_DATE]);
    setSearchValueDaysCompleted(null);
    setPageIndex(1);
  };
  const onFinish = (values) => {
    const payload = {
      mission_id: values.day,
      reward_type: values.type,
      amount: values.amount,
    };

    updateDailyReward(payload, {
      onSuccess: () => {
        toast.success('Update daily reward successfully');
        form.resetFields();
      },
      onError: () => {
        toast.error('Failed to update daily reward');
        form.resetFields();
      },
    });
  };
  const dataTable = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map((item = {}, index) => {
      return {
        id: pageIndex === 1 ? index + 1 : offset + index,
        ...item,
      };
    });
  }, [data, pageSize, offset]);
  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Space className="flex justify-between items-center">
        <AppPageTitle title="7 days login" />
      </Space>
      <AppTableQuery
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        hasDatePicker={true}
        rangePicker
        searchPlaceholder="Search ISDN"
        inputLabel="ISDN"
        timeLabel="Period"
        time={time}
        setTime={setTime}
        handleQuery={handleQuery}
        handleReset={handleReset}
        hasFilter={true}
        customFilter={
          <div
            style={{
              flex: '1 1 220px',
              maxWidth: isMobile ? '100%' : 300,
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <AppSearch
                placeholder="Search days completed"
                label="Days Completed"
                value={searchValueDaysCompleted}
                onChange={setSearchValueDaysCompleted}
                style={{ width: '100%' }}
              />
            </Space>
          </div>
        }
      />
      <AppTable
        columns={columns}
        dataSource={dataTable}
        totalItems={data?.total}
        loading={isLoading || isRefetching}
        onRefresh={refetch}
        hasPagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
      />
    </Space>
  );
}
